import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { Secret, Token } from "fernet";
import confetti from "canvas-confetti";

// Polyfill for process.env
if (typeof process === "undefined") {
  window.process = { env: {} };
}

export default function index() {
  const navigate = useNavigate();
  const [wheelData, setWheelData] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [hasSpun, setHasSpun] = useState(false);
  const [result, setResult] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [deviceMessage, setDeviceMessage] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    region: "", // Default region
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkingParticipation, setCheckingParticipation] = useState(true);
  const canvasRef = useRef(null);
  const [postSubmitMessage, setPostSubmitMessage] = useState("");

  // Check if user has already spun (local/session storage or API)
  //const key = process.env.REACT_FERNET_KEY;
  const checkSpinStatus = () => {
    const localSpun = localStorage.getItem("hasSpun");
    const sessionSpun = sessionStorage.getItem("hasSpun");
    return localSpun || sessionSpun;
  };

  // Record that user has spun
  const recordSpin = () => {
    localStorage.setItem("hasSpun", "true");
    sessionStorage.setItem("hasSpun", "true");
  };

  // Check device status using FingerprintJS
  const checkDeviceStatus = async () => {
    try {
      // Initialize the agent
      const fp = await FingerprintJS.load();
      // Get the visitor identifier
      const result = await fp.get();
      const visitorId = result.visitorId;

      // Check if user has already spun
      if (checkSpinStatus()) {
        setDeviceMessage(
          "You have already participated in the spin. Each user can only spin once."
        );
        setHasSpun(true);
      } else {
        setDeviceMessage("Welcome! You can spin the wheel once.");
      }

      // Store fingerprint in localStorage for additional verification
      localStorage.setItem("deviceFingerprint", visitorId);
    } catch (error) {
      console.error("Error getting device fingerprint:", error);
      setDeviceMessage(
        "Unable to verify device status. Please try again later."
      );
    }
  };

  // Function to check if user has already participated (API + local/session)
  const checkParticipationStatus = async () => {
    try {
      // Check local/session storage first
      if (checkSpinStatus()) {
        setDeviceMessage(
          "You have already participated in the spin. Each user can only spin once."
        );
        setHasSpun(true);
        return;
      }

      const participantId = localStorage.getItem("deviceFingerprint");
      if (!participantId) {
        setDeviceMessage("Welcome! You can spin the wheel once.");
        setHasSpun(false);
        return;
      }

      // Encrypt the payload using Fernet
      const key = "7cfJvwR2ycLS5BEApzyxsdF7-oLyYnDiYzXC1WT9lTo=";
      const secret = new Secret(key);
      const payload = {
        draw_id: "toothbrush-replacement-frequency-and-oral-health",
        participant_id: participantId,
      };
      const payloadString = JSON.stringify(payload);
      const token = new Token({
        secret: secret,
        time: Date.now() / 1000,
        iv: undefined,
      });
      const encryptedPayload = token.encode(payloadString);

      const response = await fetch(
        "https://gw.sasakonnect.net/kbackend/api/spin-participation-check/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ encrypted_data: encryptedPayload }),
        }
      );

      const encryptedResponse = await response.json();

      // Decrypt the response data
      const responseToken = new Token({
        secret: secret,
        token: encryptedResponse.encrypted_data,
        ttl: 0,
      });
      const decryptedResponse = responseToken.decode();

      const data = JSON.parse(decryptedResponse);

      if (response.ok && data.participated) {
        setDeviceMessage(
          "This device has already participated in the draw. You can only spin once."
        );
        setHasSpun(true);
        // Also record in local/session storage for redundancy
        recordSpin();
      } else {
        setDeviceMessage("Welcome! You can spin the wheel once.");
        setHasSpun(false);
      }
    } catch (error) {
      console.error("Error checking participation status:", error);
    }
  };

  // Fetch and decrypt wheel data
  const fetchWheelData = async () => {
    try {
      const response = await fetch(
        "https://gw.sasakonnect.net/kbackend/api/spin-gifts/"
      );
      const encryptedData = await response.json();

      // Initialize Fernet with the provided key
      const key = "7cfJvwR2ycLS5BEApzyxsdF7-oLyYnDiYzXC1WT9lTo=";
      const secret = new Secret(key);

      try {
        // Ensure encryptedData is a string
        const encryptedString =
          typeof encryptedData === "string"
            ? encryptedData
            : JSON.stringify(encryptedData);

        // Create a token and decrypt
        const token = new Token({
          secret: secret,
          token: encryptedString,
          ttl: 0,
        });

        const decryptedData = token.decode();

        // Parse the decrypted data
        const parsedData = JSON.parse(decryptedData);

        // Transform the data to match the expected format, preserving IDs
        const transformedData = parsedData.map((item) => ({
          id: item.id,
          prize: item.name,
          odds: parseFloat(item.odds), // Convert string odds to number
          color: item.color,
          win: item.win,
        }));

        setWheelData(transformedData);
      } catch (decryptError) {
        console.error("Decryption Error:", decryptError);
        console.error("Error Details:", {
          message: decryptError.message,
          stack: decryptError.stack,
        });
        throw decryptError;
      }
    } catch (error) {
      console.error("Error fetching wheel data:", error);
      console.error("Error Details:", {
        message: error.message,
        stack: error.stack,
      });
      // Fallback to default data if API fails
    }
  };

  // Modify the useEffect to include participation check
  useEffect(() => {
    const initialize = async () => {
      await checkDeviceStatus();
      await checkParticipationStatus();
      await fetchWheelData();
      setCheckingParticipation(false);
    };
    initialize();
  }, []);

  // Calculate total odds for probability calculations
  //const totalOdds = wheelData.reduce((sum, item) => sum + item.odds, 0);

  // Draw the wheel on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Make canvas square and responsive
    const parent = canvas.parentElement;
    const size = Math.min(parent.offsetWidth, window.innerHeight * 0.5, 400); // max 400px, fit to parent
    canvas.width = size;
    canvas.height = size;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;

    const ctx = canvas.getContext("2d");
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size / 2 - 10;

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    // Calculate equal angle for each segment
    const segmentAngle = (2 * Math.PI) / wheelData.length;
    let startAngle = 0;

    wheelData.forEach((item) => {
      // Draw segment
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, startAngle + segmentAngle);
      ctx.closePath();
      ctx.fillStyle = item.color;
      ctx.fill();
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Add text (upright, readable, auto-size, wrap if needed)
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(startAngle + segmentAngle / 2);
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      // Dynamically adjust font size based on text length and segment width
      let baseFontSize = Math.max(16, size * 0.06);
      let fontSize = baseFontSize;
      ctx.font = `bold ${fontSize}px Arial`;
      const maxTextWidth = radius * 0.7; // leave margin from edge
      // If text is too long, reduce font size
      while (
        ctx.measureText(item.prize).width > maxTextWidth &&
        fontSize > 10
      ) {
        fontSize -= 1;
        ctx.font = `bold ${fontSize}px Arial`;
      }
      // If still too long, split into two lines
      let lines = [item.prize];
      if (
        ctx.measureText(item.prize).width > maxTextWidth &&
        item.prize.includes(" ")
      ) {
        // Try to split at the last space before maxTextWidth
        const words = item.prize.split(" ");
        let line1 = words[0];
        let line2 = words.slice(1).join(" ");
        for (let i = 1; i < words.length; i++) {
          const testLine = words.slice(0, i).join(" ");
          ctx.font = `bold ${fontSize}px Arial`;
          if (ctx.measureText(testLine).width < maxTextWidth) {
            line1 = testLine;
            line2 = words.slice(i).join(" ");
          } else {
            break;
          }
        }
        lines = [line1, line2];
      }
      ctx.fillStyle = getContrastYIQ(item.color);
      ctx.shadowColor = "rgba(0,0,0,0.3)";
      ctx.shadowBlur = 2;
      // Draw one or two lines, centered vertically
      if (lines.length === 1) {
        ctx.fillText(lines[0], radius - 18, 0);
      } else {
        ctx.fillText(lines[0], radius - 18, -fontSize * 0.6);
        ctx.fillText(lines[1], radius - 18, fontSize * 0.8);
      }
      ctx.restore();

      startAngle += segmentAngle;
    });

    // Draw center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, size * 0.06, 0, 2 * Math.PI);
    ctx.fillStyle = "#ffffff";
    ctx.shadowBlur = 0;
    ctx.fill();
    ctx.strokeStyle = "#ccc";
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [wheelData, rotation]);

  // Helper for text contrast
  function getContrastYIQ(hexcolor) {
    hexcolor = hexcolor.replace("#", "");
    if (hexcolor.length === 3) {
      hexcolor = hexcolor
        .split("")
        .map((x) => x + x)
        .join("");
    }
    const r = parseInt(hexcolor.substr(0, 2), 16);
    const g = parseInt(hexcolor.substr(2, 2), 16);
    const b = parseInt(hexcolor.substr(4, 2), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? "#222" : "#fff";
  }

  // Modify the handleInputChange to check participation when phone number is entered
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Check participation status when phone number is entered
    if (name === "phone" && value.length >= 10) {
      checkParticipationStatus();
    }
  };

  // Modify the handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();
    //if (hasSpun || checkSpinStatus()) return;
    setIsSubmitting(true);

    const participantId =
      localStorage.getItem("deviceFingerprint") || "user" + Date.now();
    const submissionData = {
      draw_id: "toothbrush-replacement-frequency-and-oral-health",
      participant_id: participantId,
      item_won_id: result.id,
      item_won: result.prize,
      name: formData.name,
      phone: formData.phone,
      region: formData.region,
    };

    try {
      const response = await fetch(
        "https://gw.sasakonnect.net/kbackend/api/spin-participate/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submissionData),
        }
      );

      // const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          `Failed to submit participation: ${response.status} ${response.statusText}`
        );
      }

      // Show a thank you/confirmation message after submitting
      setPostSubmitMessage(
        "Thank you for submitting your details! We have received your information."
      );
      setShowForm(false);
      // Redirect after a short delay (e.g., 2 seconds)
      setTimeout(() => {
        setPostSubmitMessage("");
        //navigate("/");
      }, 2000);
    } catch (error) {
      console.error("Error submitting form:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
      });
      alert(`Failed to submit your information: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modify the spinWheel function to check participation status
  const spinWheel = async () => {
    if (spinning || hasSpun || checkSpinStatus()) return;

    try {
      // Check participation status before spinning

      setSpinning(true);

      // Determine the winning item based on odds
      let oddsArray = [];
      wheelData.forEach((item, index) => {
        for (let i = 0; i < item.odds; i++) {
          oddsArray.push(index);
        }
      });

      if (oddsArray.length === 0) {
        setResult({ prize: "No prizes available", color: "#cccccc", id: null });
        setSpinning(false);
        setHasSpun(true);
        return;
      }

      // Random selection based on odds
      const winningIndex =
        oddsArray[Math.floor(Math.random() * oddsArray.length)];
      const winningItem = wheelData[winningIndex];

      // If the user did not win (e.g., prize is 'Try Again' or odds is 0)
      // if (
      //   !winningItem.win ||
      //   winningItem.prize.toLowerCase().includes("No Win") ||
      //   winningItem.odds === 0
      // ) {
      //   setTimeout(() => {
      //     setPostSubmitMessage(
      //       "Sorry, you did not win this time. Please try again next time!"
      //     );
      //   }, 500);
      // }

      // Calculate rotation to land on winning segment
      const segmentSize = 360 / wheelData.length;
      // Align the center of the winning segment with the pointer (top)
      const targetPosition = winningIndex * segmentSize + segmentSize / 2;
      const fullRotations = 8 + Math.floor(Math.random() * 4);
      const finalRotation = fullRotations * 360 + (360 - targetPosition) - 90;

      const spinDuration = 10000;
      const spinStart = Date.now();
      let lastRotation = 0;

      const animate = () => {
        const now = Date.now();
        const elapsed = now - spinStart;

        if (elapsed < spinDuration) {
          const progress = elapsed / spinDuration;
          const easeOut = progress < 0.95 ? 1 - Math.pow(1 - progress, 4) : 1;

          const currentRotation = easeOut * finalRotation;

          if (Math.abs(currentRotation - lastRotation) > 0.1) {
            setRotation(currentRotation);
            lastRotation = currentRotation;
          }

          requestAnimationFrame(animate);
        } else {
          setRotation(finalRotation);
          setSpinning(false);
          setHasSpun(true);

          // Store the winning item with its ID
          setResult({
            prize: winningItem.prize,
            color: winningItem.color,
            id: winningItem.id,
            win: winningItem.win,
          });

          // Record that user has spun
          recordSpin();

          // If it's a winning item, show confetti and form
          if (winningItem.win) {
            // Trigger confetti
            confetti({
              particleCount: 100,
              spread: 70,
              origin: { y: 0.6 },
            });
            setShowForm(true);
          } else {
            // Show try again message in the UI
            setPostSubmitMessage(
              "Sorry, you didn't win this time. Please wait for the next survey!"
            );
          }
        }
      };

      requestAnimationFrame(animate);
    } catch (error) {
      console.error("Error during spin:", error);
      setDeviceMessage("An error occurred. Please try again later.");
      setSpinning(false);
    }
  };

  return (
    <div className="container mt-4">
      {checkingParticipation ? (
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Checking participation status...</p>
          </div>
        </div>
      ) : (
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 text-center">
            <h1 className="mb-4">Spin the Wheel</h1>

            {deviceMessage && (
              <div
                className={`alert ${
                  hasSpun ? "alert-warning" : "alert-info"
                } mb-4`}
              >
                {deviceMessage}
              </div>
            )}

            {/* Only show the wheel and spin button if the user hasn't spun */}
            {!hasSpun && (
              <>
                {/* Responsive wheel container */}
                <div
                  className="position-relative mx-auto mb-4"
                  style={{
                    width: "100%",
                    maxWidth: "400px",
                    aspectRatio: "1/1",
                    minWidth: "220px",
                    minHeight: "220px",
                    display: "block",
                  }}
                >
                  {/* Maintain aspect ratio for responsiveness */}
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      paddingTop: "100%",
                    }}
                  >
                    {/* The wheel */}
                    <canvas
                      ref={canvasRef}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                        background: "#fff",
                        transform: `rotate(${rotation}deg)`,
                      }}
                    />
                    {/* SVG Arrow Pointer - always inside the wheel, scalable */}
                    <svg
                      viewBox="0 0 60 60"
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "16%",
                        transform: "translate(-50%, 0)",
                        width: "18%",
                        height: "18%",
                        zIndex: 21,
                      }}
                    >
                      <polygon
                        points="30,0 40,30 30,24 20,30"
                        fill="#FFA500"
                        stroke="#fff"
                        strokeWidth="2"
                        style={{
                          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.18))",
                        }}
                      />
                    </svg>
                    {/* Center dot */}
                    <div
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: "clamp(16px, 4vw, 24px)",
                        height: "clamp(16px, 4vw, 24px)",
                        borderRadius: "50%",
                        background:
                          "radial-gradient(circle, #FFD700 60%, #FFA500 100%)",
                        transform: "translate(-50%, -50%)",
                        zIndex: 22,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                        border: "2.5px solid #fff",
                      }}
                    ></div>
                  </div>
                </div>

                {/* Spin button always below the wheel, with spacing */}
                <div className="mb-4" style={{ marginTop: 24 }}>
                  <button
                    onClick={() => {
                      sessionStorage.setItem("canAccessWheel", "true");
                      spinWheel();
                    }}
                    disabled={spinning || hasSpun || checkingParticipation}
                    className={`btn btn-primary btn-lg ${
                      spinning ? "spinning" : ""
                    }`}
                    style={{
                      minWidth: "200px",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {spinning ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Spinning...
                      </>
                    ) : (
                      "Spin the Wheel"
                    )}
                  </button>
                </div>
              </>
            )}

            {/* If the user has spun, show a Go Home button or catchy CTA */}

            {result && result.win && !showForm && (
              <div className="alert alert-success mb-4 animate__animated animate__fadeIn">
                <h4 className="mb-2">Congratulations! 🎉</h4>
                <p className="mb-0">
                  You won: <strong>{result.prize}</strong>
                </p>
              </div>
            )}

            {result && !result.win && (
              <div className="alert alert-info mb-4 animate__animated animate__fadeIn">
                <h4 className="mb-2">Better luck next time!</h4>
                <p className="mb-0">
                  Please wait for the next survey to try again.
                </p>
              </div>
            )}

            {hasSpun && (
              <div className="my-5">
                <h2 className="mb-4">Thank you for participating!</h2>
                <button
                  className="btn btn-success btn-lg"
                  onClick={() => navigate("/")}
                >
                  Home
                </button>
                <div className="mt-3">
                  <span role="img" aria-label="party">
                    🎉
                  </span>
                  <strong> You will be contacted to claim your prize!</strong>
                </div>
              </div>
            )}

            {showForm && (
              <div
                className="modal show d-block"
                tabIndex="-1"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Claim Your Prize</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowForm(false)}
                        disabled={isSubmitting}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <div className="alert alert-success mb-3">
                        <h6 className="mb-1">Congratulations! 🎉</h6>
                        <p className="mb-0">
                          You won: <strong>{result.prize}</strong>
                        </p>
                      </div>
                      <form>
                        <div className="mb-3">
                          <label htmlFor="name" className="form-label">
                            Full Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="mb-3">
                          <label htmlFor="phone" className="form-label">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            className="form-control"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="mb-3">
                          <label htmlFor="region" className="form-label">
                            Region
                          </label>
                          <select
                            className="form-select"
                            id="region"
                            name="region"
                            value={formData.region}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">-- Select Region --</option>
                            <option value="G44">Githurai 44</option>
                            <option value="G45">Githurai 45</option>
                            <option value="Zimmerman">Zimmerman</option>
                            <option value="Roysambu">Roysambu/Mirema</option>
                            <option value="Kahawa_West">Kahawa West</option>
                            <option value="Hunters_Kasarani">
                              Hunters/Kasarani
                            </option>
                            <option value="Lucky_Summer">Lucky Summer</option>
                            <option value="Umoja_1">Umoja 1</option>
                            <option value="Umoja_2">Umoja 2</option>
                            <option value="Umoja_3">Umoja 3</option>
                            <option value="Pipeline">Pipeline</option>
                            <option value="Kayole">Kayole</option>
                            <option value="Rongai">Rongai</option>
                            <option value="Kibera">Kibera</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div className="d-grid">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                          >
                            {isSubmitting ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Submitting...
                              </>
                            ) : (
                              "Submit"
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {postSubmitMessage && (
              <div className="alert alert-info text-center my-4 animate__animated animate__fadeIn">
                {postSubmitMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
